import { getConfig } from "./runtimeConfig";

export type FeatureType =
  | "validateResource"
  | "ruleExecution"
  | "researchQuery";

export interface RunParameterFieldDef {
  key: string;
  label: string;
  type: "number" | "boolean" | "string";
  defaultValue: any;
  description: string;
}

export const BACKEND_QUERY_PARAMS: Record<FeatureType, RunParameterFieldDef[]> =
  {
    validateResource: [
      {
        key: "enable_column_slimming",
        label: "Enable Column Slimming",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, reduces the number of columns stored in the output by trimming non-essential columns, improving storage efficiency.",
      },
      {
        key: "enable_parallel_derived_columns",
        label: "Enable Parallel Derived Columns",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, derived columns are computed in parallel for faster processing.",
      },
      {
        key: "generate_files",
        label: "Generate Output Files",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, generates output files (e.g., validation reports) during execution.",
      },
      {
        key: "generate_score",
        label: "Generate Score",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, generates a score after a successful execution. Disable to skip score generation.",
      },
      {
        key: "generate_separate_log_file",
        label: "Generate Separate Log File",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, generates a separate log file for the execution in addition to the standard logging.",
      },
      {
        key: "impact_issues",
        label: "Impact Issues",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, creates or updates linked issues based on the execution results. Requires 'Store All Data Columns' to be on.",
      },
      {
        key: "key_only_mode",
        label: "Key Only Mode",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, processes only key columns for execution where applicable.",
      },
      {
        key: "is_long_running_job",
        label: "Long Running Job",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, the execution is routed to the long-running job worker for better handling of large data sets.",
      },
      {
        key: "keep_downloaded_files",
        label: "Keep Downloaded Files",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, files downloaded from external sources (FTP, blob, etc.) are retained after execution.",
      },
      {
        key: "no_of_errors_in_filter_record_output_files",
        label: "No Of Errors In Filter Record Output Files",
        type: "number",
        defaultValue: 0,
        description:
          "Maximum number of errors to write to filtered record output files. Set to 0 to write all.",
      },
      {
        key: "no_of_errors_in_output_files",
        label: "No Of Errors In Output Files",
        type: "number",
        defaultValue: 0,
        description:
          "Maximum number of errors to write to output files. Set to 0 to write all errors.",
      },
      {
        key: "no_of_errors_in_response",
        label: "No Of Errors In Response",
        type: "number",
        defaultValue: 0,
        description:
          "Maximum number of validation errors to include in the API response. Set to 0 to return all errors.",
      },
      {
        key: "no_of_sample_validation_errors",
        label: "No Of Sample Validation Errors",
        type: "number",
        defaultValue: 0,
        description:
          "Number of sample validation errors to include for each validation rule. Set to 0 to include all samples.",
      },
      {
        key: "pull_new_files_from_server",
        label: "Pull New Files From Server",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, pulls fresh files from the configured server before execution instead of using cached files.",
      },
      {
        key: "save_input_data_file",
        label: "Generate Input Files",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, saves the input data file used during execution for later reference.",
      },
      {
        key: "skip_duplicate_records",
        label: "Skip Duplicate Records",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, duplicate records in the resource data are skipped during validation.",
      },
      {
        key: "store_all_data_columns",
        label: "Store All Data Columns",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, stores all data columns in the execution results. When disabled, only key columns are stored.",
      },
      {
        key: "summary_mode",
        label: "Summary Mode",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, returns only a summary of validation results instead of detailed error records.",
      },
      {
        key: "is_masking_enabled",
        label: "Enable Masking",
        type: "boolean",
        defaultValue: false,
        description: "When enabled, returns only masked data.",
      },
    ],
    ruleExecution: [
      {
        key: "enable_column_slimming",
        label: "Enable Column Slimming",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, reduces the number of columns stored in the output by trimming non-essential columns, improving storage efficiency.",
      },
      {
        key: "enable_parallel_derived_columns",
        label: "Enable Parallel Derived Columns",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, derived columns are computed in parallel for faster processing.",
      },
      {
        key: "execute_comparison_research_query",
        label: "Execute Research Queries",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, executes any comparison research queries linked to the rule after the main comparison.",
      },
      {
        key: "generate_files",
        label: "Generate Output Files",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, generates output files (e.g., comparison reports) during execution.",
      },
      {
        key: "generate_score",
        label: "Generate Score",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, generates a score after a successful execution. Disable to skip score generation.",
      },
      {
        key: "generate_separate_log_file",
        label: "Generate Separate Log File",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, generates a separate log file for the execution in addition to the standard logging.",
      },
      {
        key: "impact_issues",
        label: "Impact Issues",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, creates or updates linked issues based on the execution results. Requires 'Store All Data Columns' to be on.",
      },
      {
        key: "key_only_mode",
        label: "Key Only Mode",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, processes only key columns for execution where applicable.",
      },
      {
        key: "is_long_running_job",
        label: "Long Running Job",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, the execution is routed to the long-running job worker for better handling of large data sets.",
      },
      {
        key: "keep_downloaded_files",
        label: "Keep Downloaded Files",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, files downloaded from external sources (FTP, blob, etc.) are retained after execution.",
      },
      {
        key: "no_of_errors_in_filter_record_output_files",
        label: "No Of Errors In Filter Record Output Files",
        type: "number",
        defaultValue: 0,
        description:
          "Maximum number of errors to write to filtered record output files. Set to 0 to write all.",
      },
      {
        key: "no_of_errors_in_output_files",
        label: "No Of Errors In Output Files",
        type: "number",
        defaultValue: 0,
        description:
          "Maximum number of errors to write to output files. Set to 0 to write all errors.",
      },
      {
        key: "no_of_errors_in_response",
        label: "No Of Errors In Response",
        type: "number",
        defaultValue: 0,
        description:
          "Maximum number of validation errors to include in the API response. Set to 0 to return all errors.",
      },
      {
        key: "no_of_sample_validation_errors",
        label: "No Of Sample Validation Errors",
        type: "number",
        defaultValue: 0,
        description:
          "Number of sample validation errors to include for each validation rule. Set to 0 to include all samples.",
      },
      {
        key: "pull_new_files_from_server",
        label: "Pull New Files From Server",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, pulls fresh files from the configured server before execution instead of using cached files.",
      },
      {
        key: "save_input_data_file",
        label: "Generate Input Files",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, saves the input data file used during execution for later reference.",
      },
      {
        key: "skip_duplicate_records",
        label: "Skip Duplicate Records",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, duplicate records in the resource data are skipped during processing.",
      },
      {
        key: "skip_validation",
        label: "Skip Validation",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, skips the validation step and only runs the comparison/aggregation logic.",
      },
      {
        key: "store_all_data_columns",
        label: "Store All Data Columns",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, stores all data columns in the execution results. When disabled, only key columns are stored.",
      },
      {
        key: "summary_mode",
        label: "Summary Mode",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, returns only a summary of validation results instead of detailed error records.",
      },
      {
        key: "use_merge_on_keys_as_unique_keys",
        label: "Use Merge On Keys As Unique Keys",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, merge-on keys are also used as unique keys for identifying records during resource processing.",
      },
      {
        key: "use_secondary_merge_resources",
        label: "Use Secondary Merge Resources",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, uses secondary merge resources configured in the rule during comparison.",
      },
      {
        key: "is_masking_enabled",
        label: "Enable Masking",
        type: "boolean",
        defaultValue: false,
        description: "When enabled, returns only masked data.",
      },
    ],
    researchQuery: [
      {
        key: "enable_parallel_derived_columns",
        label: "Enable Parallel Derived Columns",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, derived columns are computed in parallel for faster processing.",
      },
      {
        key: "generate_output_files",
        label: "Generate Output Files",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, generates output files (e.g., research query reports) during execution.",
      },
      {
        key: "generate_score",
        label: "Generate Score",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, generates a score after a successful execution. Disable to skip score generation.",
      },
      {
        key: "generate_separate_log_file",
        label: "Generate Separate Log File",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, generates a separate log file for the execution in addition to the standard logging.",
      },
      {
        key: "impact_issues",
        label: "Impact Issues",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, creates or updates linked issues based on the execution results. Requires 'Store All Data Columns' to be on.",
      },
      {
        key: "is_long_running_job",
        label: "Long Running Job",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, the execution is routed to the long-running job worker for better handling of large data sets.",
      },
      {
        key: "keep_downloaded_files",
        label: "Keep Downloaded Files",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, files downloaded from external sources (FTP, blob, etc.) are retained after execution.",
      },
      {
        key: "no_of_records_in_filter_rule_output_files",
        label: "No Of Records In Filter Rule Output Files",
        type: "number",
        defaultValue: 0,
        description:
          "Maximum number of records to write to filtered rule output files. Set to 0 to write all.",
      },
      {
        key: "no_of_records_in_output_files",
        label: "No Of Records In Output Files",
        type: "number",
        defaultValue: 0,
        description:
          "Maximum number of records to write to output files. Set to 0 to write all records.",
      },
      {
        key: "no_of_records_in_response",
        label: "No Of Records In Response",
        type: "number",
        defaultValue: 0,
        description:
          "Maximum number of records to include in the API response. Set to 0 to return all records.",
      },
      {
        key: "pull_latest_files",
        label: "Pull Latest Files",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, fetches the latest version of files from the source before execution.",
      },
      {
        key: "pull_new_files_from_server",
        label: "Pull New Files From Server",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, pulls fresh files from the configured server before execution instead of using cached files.",
      },
      {
        key: "report_name",
        label: "Report Name",
        type: "string",
        defaultValue: "generic_research_query_report",
        description: "Name for the generated research query report file.",
      },
      {
        key: "save_input_data_files",
        label: "Save Input Data Files",
        type: "boolean",
        defaultValue: false,
        description:
          "When enabled, saves the input data files used during execution for later reference.",
      },
      {
        key: "store_all_data_columns",
        label: "Store All Data Columns",
        type: "boolean",
        defaultValue: true,
        description:
          "When enabled, stores all data columns in the execution results. When disabled, only key columns are stored.",
      },
    ],
  };

/**
 * Get fields sorted alphabetically by label
 */
export const getSortedFields = (
  feature: FeatureType,
): RunParameterFieldDef[] => {
  return [...BACKEND_QUERY_PARAMS[feature]].sort((a, b) =>
    a.label.localeCompare(b.label),
  );
};

/**
 * Get fields of a specific type, sorted alphabetically
 */
export const getSortedFieldsByType = (
  feature: FeatureType,
  type: "number" | "boolean" | "string",
): RunParameterFieldDef[] => {
  return BACKEND_QUERY_PARAMS[feature]
    .filter((f) => f.type === type)
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Body-level fields (not in query_params) with labels and descriptions
 */
export const BODY_LEVEL_FIELDS: Record<FeatureType, RunParameterFieldDef[]> = {
  validateResource: [
    {
      key: "pull_latest_files",
      label: "Pull Latest Files",
      type: "boolean",
      defaultValue: false,
      description:
        "When enabled, fetches the latest version of files from the source before execution.",
    },
    {
      key: "sample_size",
      label: "Sample Size",
      type: "number",
      defaultValue: 0,
      description:
        "Number of records to randomly sample from the resource data for validation. Only used when Sampling Validation is enabled.",
    },
    {
      key: "sampling_validation",
      label: "Sampling Validation",
      type: "boolean",
      defaultValue: false,
      description:
        "When enabled, validates only a random sample of the resource data instead of the full dataset.",
    },
  ],
  ruleExecution: [
    {
      key: "pivot_results",
      label: "Pivot Results",
      type: "boolean",
      defaultValue: false,
      description:
        "When enabled, pivots (transposes) the comparison results for rules with 5 or fewer numeric columns, making them easier to read.",
    },
    {
      key: "pull_latest_files",
      label: "Pull Latest Files",
      type: "boolean",
      defaultValue: false,
      description:
        "When enabled, fetches the latest version of files from the source before execution.",
    },
  ],
  researchQuery: [],
};

/**
 * Get all fields (query_params + body-level) sorted alphabetically by label
 */
export const getAllSortedFields = (
  feature: FeatureType,
): RunParameterFieldDef[] => {
  const allFields = [
    ...BACKEND_QUERY_PARAMS[feature],
    ...BODY_LEVEL_FIELDS[feature],
  ];
  return allFields.sort((a, b) => a.label.localeCompare(b.label));
};

export const getFieldDescription = (
  feature: FeatureType,
  fieldKey: string,
): string => {
  const queryField = BACKEND_QUERY_PARAMS[feature]?.find(
    (f) => f.key === fieldKey,
  );
  if (queryField) return queryField.description;
  const bodyField = BODY_LEVEL_FIELDS[feature]?.find((f) => f.key === fieldKey);
  if (bodyField) return bodyField.description;
  return "";
};

export const getFieldLabel = (
  feature: FeatureType,
  fieldKey: string,
): string => {
  const queryField = BACKEND_QUERY_PARAMS[feature]?.find(
    (f) => f.key === fieldKey,
  );
  if (queryField) return queryField.label;
  const bodyField = BODY_LEVEL_FIELDS[feature]?.find((f) => f.key === fieldKey);
  if (bodyField) return bodyField.label;
  return fieldKey;
};

export const FE_ONLY_FIELDS: Record<FeatureType, any> = {
  validateResource: {
    validation_severity_level: "Low",
    run_instance: {
      run_id: Number(getConfig("REACT_APP_DEFAULT_RUN_INSTANCE_ID")) || 1,
      run_name: getConfig("REACT_APP_DEFAULT_RUN_INSTANCE_NAME", "Legacy"),
    },
    execution_sub_group: {
      execution_sub_group_id:
        Number(getConfig("REACT_APP_DEFAULT_EXECUTION_SUB_GROUP_ID")) || 1,
      execution_sub_group_code: getConfig(
        "REACT_APP_DEFAULT_EXECUTION_SUB_GROUP_CODE",
        "default",
      ),
    },
    isCustomiseSeverity: false,
    skip_validation: false, // validateResource uses RunParameters component which requires skip_validation in state, though it's hidden and unused
    pull_latest_files: false, // Body-level field on ValidationRequest, not a query_param
    sampling_validation: false, // Body-level field on ValidationRequest
    sample_size: 0, // Body-level field on ValidationRequest
  },
  ruleExecution: {
    validation_severity_level: "Low",
    run_instance: {
      run_id: Number(getConfig("REACT_APP_DEFAULT_RUN_INSTANCE_ID")) || 1,
      run_name: getConfig("REACT_APP_DEFAULT_RUN_INSTANCE_NAME", "Legacy"),
    },
    execution_sub_group: {
      execution_sub_group_id:
        Number(getConfig("REACT_APP_DEFAULT_EXECUTION_SUB_GROUP_ID")) || 1,
      execution_sub_group_code: getConfig(
        "REACT_APP_DEFAULT_EXECUTION_SUB_GROUP_CODE",
        "default",
      ),
    },
    isCustomiseSeverity: false,
    pull_latest_files: false, // Body-level field on RuleExecutionRequest, not a query_param
    pivot_results: false, // Body-level field on RuleExecutionRequest
  },
  researchQuery: {
    run_instance: {
      run_id: Number(getConfig("REACT_APP_DEFAULT_RUN_INSTANCE_ID")) || 1,
      run_name: getConfig("REACT_APP_DEFAULT_RUN_INSTANCE_NAME", "Legacy"),
    },
    execution_sub_group: {
      execution_sub_group_id:
        Number(getConfig("REACT_APP_DEFAULT_EXECUTION_SUB_GROUP_ID")) || 1,
      execution_sub_group_code: getConfig(
        "REACT_APP_DEFAULT_EXECUTION_SUB_GROUP_CODE",
        "default",
      ),
    },
  },
};

export const getDefaultRunParameters = (feature: FeatureType) => {
  const defaults: any = {};
  BACKEND_QUERY_PARAMS[feature].forEach((field) => {
    defaults[field.key] = field.defaultValue;
  });
  const feOnly = FE_ONLY_FIELDS[feature];
  return { ...defaults, ...feOnly };
};

export const getQueryParams = (feature: FeatureType, runParameters: any) => {
  const queryParams: any = {};
  BACKEND_QUERY_PARAMS[feature].forEach((field) => {
    if (
      runParameters[field.key] !== undefined &&
      runParameters[field.key] !== null
    ) {
      queryParams[field.key] = runParameters[field.key];
    } else {
      queryParams[field.key] = field.defaultValue;
    }
  });
  return queryParams;
};

export const getVisibleFields = (feature: FeatureType) => {
  if (feature === "validateResource") {
    return {
      isShowSkipValidation: false,
      isShowExecuteResearchQueries: false,
      use_secondary_merge_resources: false,
    };
  } else if (feature === "ruleExecution") {
    return {
      isShowSkipValidation: true,
      isShowExecuteResearchQueries: true,
      use_secondary_merge_resources: true,
    };
  } else {
    // researchQuery
    return {
      isShowReportName: true,
    };
  }
};
